import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Group,
  Indicator,
  List,
  Space,
  Sx,
  Text as MantineText,
  ThemeIcon,
  useMantineTheme,
} from '@mantine/core';
import { FC, useContext, useState } from 'react';
import { IconCheck, IconPackage, IconUsers } from '@tabler/icons';
// eslint-disable-next-line import/no-unresolved
import { A11y, Navigation, Pagination } from 'swiper/modules';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import PanePaper from '../../../Molecules/PanePaper';
import { DataS } from '../../../Service/DataS';
import { AddonDTO, BillingAddon, BillingInterval } from '../../../Types/Billing';
import { ResourceAddonContext } from '../../../Service/Context/ResourceAddonsContext';
import { BookitupSubscriptionContext } from '../../../Service/Context/BookitupSubscriptionContext';
import { IncDecCounterv2 } from '../../../Atoms/inputs/IncDecCounter';
import { ChargebeeS } from '../../../Service/ChargebeeS';
import { AuthContext } from '../../../Service/Context/AuthContext';
import { WindowContext } from '../../../Service/Context/WindowContext';

const RESOURCE_ADDON_NAMES = ['Resource-Planning', 'Ressourcen'];
const ACTIVE_BILLING_COLOR = '#FE8047';

const mobileCardSwiperSx: Sx = (theme) => ({
  marginLeft: -4,
  marginRight: -4,
  '.swiper': {
    paddingLeft: 4,
    paddingRight: 4,
    paddingBottom: 30,
    overflow: 'visible',
  },
  '.swiper-wrapper': {
    alignItems: 'stretch',
  },
  '.swiper-slide': {
    height: 'auto',
    display: 'flex',
  },
  '.swiper-slide > .mantine-Indicator-root': {
    width: '100%',
    maxWidth: '100%',
  },
  '.swiper-pagination': {
    bottom: 0,
  },
  '.swiper-button-prev, .swiper-button-next': {
    width: 32,
    height: 32,
    marginTop: -28,
    borderRadius: 32,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.white,
    boxShadow: theme.colorScheme === 'dark' ? '0 5px 16px rgba(0, 0, 0, 0.3)' : '0 5px 16px rgba(29, 81, 145, 0.16)',
    color: ACTIVE_BILLING_COLOR,
    '&::after': {
      fontSize: 13,
      fontWeight: 900,
    },
  },
  '.swiper-button-prev': {
    left: 0,
  },
  '.swiper-button-next': {
    right: 0,
  },
  '.swiper-button-disabled': {
    opacity: 0.28,
  },
  '.swiper-pagination-bullet': {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[4],
    opacity: 1,
  },
  '.swiper-pagination-bullet-active': {
    backgroundColor: ACTIVE_BILLING_COLOR,
  },
});

const normalizeAddonName = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

const isResourceAddonName = (name: string) =>
  RESOURCE_ADDON_NAMES.some((addonName) => normalizeAddonName(name).startsWith(normalizeAddonName(addonName)));

const findActiveAddon = (addons: AddonDTO[] | undefined, addonName: string) => {
  if (!addons || addons.length === 0) return undefined;
  const normalizedAddonName = normalizeAddonName(addonName);
  return addons.find((addon) => {
    const normalizedActiveAddonName = normalizeAddonName(addon.name);
    return (
      normalizedActiveAddonName.startsWith(normalizedAddonName) ||
      (isResourceAddonName(addonName) && isResourceAddonName(addon.name))
    );
  });
};

const AddonPriceRow: FC<{ label: string; amount: number }> = ({ label, amount }) => (
  <>
    <Group position="apart" spacing="xs">
      <MantineText size="xs" weight="lighter">
        {label}
      </MantineText>
      <MantineText size="xs" weight="bolder">
        {amount} €
      </MantineText>
    </Group>
    <Divider my={3} />
  </>
);

const AddonPrice = ({ addon, billingInterval }: { addon: BillingAddon; billingInterval: BillingInterval }) => {
  const { price } = addon;

  if (price.strategy === 'perUnit') {
    const amount = price.rates[billingInterval];
    return (
      <>
        <MantineText align="center" size="xl">
          {amount} € / {price.unitLabel} / Monat
        </MantineText>
        <MantineText align="center" size="xs" color="dimmed">
          zzgl. gesetzl. MwSt.
        </MantineText>
        {/* <MantineText size="sm" weight="bolder" mt="xl">
          Preis pro {price.unitLabel}
        </MantineText>
        <Space h="sm" /> */}
        {/* <List
          spacing={5}
          size="xs"
          icon={
            <ThemeIcon radius="xl" size="xs">
              <IconCheck size={10} />
            </ThemeIcon>
          }
        >
          <AddonPriceRow label={`1 ${price.unitLabel}`} amount={price.rates[billingInterval]} />
        </List> */}
      </>
    );
  }

  const startingPrice = price.tiers[billingInterval][0]?.price;

  return (
    <>
      {startingPrice && (
        <>
          <MantineText align="center" size="xl">{`ab ${startingPrice} € / Monat`}</MantineText>
          <MantineText align="center" size="xs" color="dimmed">
            zzgl. gesetzl. MwSt.
          </MantineText>
        </>
      )}

      <Space h="sm" />
      <PanePaper>
        <MantineText size="xs" weight="bolder" mb="sm" mt={-5}>
          Anzahl der {price.unitLabel}
        </MantineText>
        {price.tiers[billingInterval].map((tier) => {
          const { from, to, price: tierAmount } = tier;
          return <AddonPriceRow key={tierAmount} label={`${from} - ${to}`} amount={tierAmount} />;
        })}
      </PanePaper>
    </>
  );
};

const AddonFeatures: FC<{ features: string[] }> = ({ features }) => (
  <>
    <Divider />
    <Space h="sm" />
    <List spacing={5} size="xs" icon={<IconCheck size={12} stroke={3} color="#FDAF47" />}>
      {features.map((feature) => (
        <List.Item key={feature}>{feature}</List.Item>
      ))}
    </List>
  </>
);

const getAddonQuantitySteps = (addon: BillingAddon, billingInterval: BillingInterval) => {
  if (addon.price.strategy !== 'tiered') return undefined;
  return addon.price.tiers[billingInterval].map((tier) => tier.from);
};

const formatAddonQuantity = (addon: BillingAddon, billingInterval: BillingInterval, quantity: number) => {
  if (addon.price.strategy !== 'tiered') return quantity;
  const tier = addon.price.tiers[billingInterval].find(({ from, to }) => quantity >= from && quantity <= to);
  return tier ? `${tier.from} - ${tier.to}` : quantity;
};

const getAddonCheckoutQuantity = (addon: BillingAddon, billingInterval: BillingInterval, quantity: number) => {
  if (addon.price.strategy !== 'tiered') return quantity;
  const tier = addon.price.tiers[billingInterval].find(({ from, to }) => quantity >= from && quantity <= to);
  return tier?.to ?? quantity;
};

const AvailableAddonCard: FC<{
  addon: BillingAddon;
  activeAddons?: AddonDTO[];
  billingInterval: BillingInterval;
  onAddonCheckoutSuccess: () => void;
  openTrialModal: () => unknown;
  hasActiveLicence: boolean;
  trialAvailable: boolean;
}> = ({
  addon,
  activeAddons,
  billingInterval,
  onAddonCheckoutSuccess,
  openTrialModal,
  hasActiveLicence,
  trialAvailable,
}) => {
  const [quantity, setQuantity] = useState(1);
  const { colors, colorScheme } = useMantineTheme();
  const { name, internalName, shortDescription, features } = addon;
  const activeAddon = findActiveAddon(activeAddons, name);
  const isResourceAddon = isResourceAddonName(name);
  const isTeamAddon = normalizeAddonName(name) === 'team';
  const isAddonActive = Boolean(activeAddon);
  const canChangeBillingInterval = isAddonActive && activeAddon?.interval !== billingInterval;
  const canAddAddon = !activeAddon;
  const quantitySteps = getAddonQuantitySteps(addon, billingInterval);

  const openAddonCheckout = () => {
    const addonId = `${internalName}-EUR-${billingInterval}`;
    const checkoutQuantity = getAddonCheckoutQuantity(addon, billingInterval, activeAddon?.quantity ?? quantity);
    ChargebeeS.openHostedAddonCheckout(addonId, checkoutQuantity, onAddonCheckoutSuccess);
  };

  const card = (
    <PanePaper
      sx={{
        width: '100%',
        height: '100%',
        border: `${activeAddon ? '2px' : '1px'} solid ${
          // eslint-disable-next-line no-nested-ternary
          activeAddon ? ACTIVE_BILLING_COLOR : colorScheme === 'dark' ? colors.dark[4] : colors.gray[1]
        }`,
      }}
    >
      <Group spacing="md" noWrap>
        <ThemeIcon radius="md" size="lg" variant="light" color="#DFEBFB">
          {isTeamAddon ? <IconUsers size={20} /> : <IconPackage size={20} />}
        </ThemeIcon>
        <MantineText weight={700} size={18}>
          {name}
        </MantineText>
      </Group>
      <MantineText color="dimmed" mt="md" size="xs">
        {shortDescription}
      </MantineText>
      <Space h="xl" />
      <AddonPrice addon={addon} billingInterval={billingInterval} />
      <Space h="xs" />

      {(canAddAddon || canChangeBillingInterval) && (
        <>
          <PanePaper>
            {canAddAddon && (
              <IncDecCounterv2
                initialCount={quantity}
                min={1}
                deleteDisabled
                onChanged={setQuantity}
                unit={addon.price.unitLabel}
                steps={quantitySteps}
                formatValue={(count) => formatAddonQuantity(addon, billingInterval, count)}
              />
            )}
            <Center>
              <Button
                variant="outline"
                size="xs"
                mt={canAddAddon ? 'xl' : 0}
                disabled={!hasActiveLicence}
                onClick={openAddonCheckout}
                sx={{ width: 200 }}
              >
                {canChangeBillingInterval ? 'Abrechnungsintervall ändern' : 'Addon hinzufügen'}
              </Button>
            </Center>

            {!hasActiveLicence && (
              <MantineText size={10} mt={1} color="orange" align="center">
                Abonnement erforderlich
              </MantineText>
            )}
          </PanePaper>
          <Space h="xl" />
        </>
      )}
      {isResourceAddon && trialAvailable && !activeAddon && (
        <Center mt="xl" mb="xl">
          <Button variant="subtle" onClick={openTrialModal}>
            Jetzt kostenlos testen
          </Button>
        </Center>
      )}
      <AddonFeatures features={features} />
    </PanePaper>
  );

  return (
    <Indicator
      disabled={!isAddonActive && !isTeamAddon}
      label={
        <MantineText color="white" size={11} weight="bold">
          {isAddonActive ? 'Aktiv' : 'Neu'}
        </MantineText>
      }
      position="top-center"
      radius="md"
      size={18}
      styles={(theme) => ({
        indicator: {
          backgroundColor: ACTIVE_BILLING_COLOR,
          color: theme.white,
          width: 50,
        },
      })}
      sx={{
        flex: '1 1 250px',
        maxWidth: 300,
        width: '100%',
        '@media (max-width: 576px)': {
          maxWidth: '100%',
        },
      }}
    >
      {card}
    </Indicator>
  );
};

const AvailableAddons: FC<{ billingInterval: BillingInterval }> = ({ billingInterval }) => {
  const { refreshToken } = useContext(AuthContext);
  const { sm } = useContext(WindowContext);
  const { trialAvailable, openTrialModal, refresh: refreshAddon } = useContext(ResourceAddonContext);
  const { subscription, refresh: refreshSubscription } = useContext(BookitupSubscriptionContext);
  const activeAddons = subscription?.addons;
  const hasActiveLicence = subscription?.status === 'ACTIVE';
  const addons = DataS.billingAddons;

  const onAddonCheckoutSuccess = () => {
    refreshToken();
    refreshSubscription();
    refreshAddon();
  };

  return (
    <div>
      {sm ? (
        <Box sx={mobileCardSwiperSx}>
          <Swiper
            modules={[Navigation, Pagination, A11y]}
            navigation
            pagination={{ clickable: true, dynamicBullets: addons.length > 4 }}
            slidesPerView={1.08}
            spaceBetween={16}
            centeredSlides
            grabCursor
          >
            {addons.map((addon) => (
              <SwiperSlide key={addon.name}>
                <AvailableAddonCard
                  addon={addon}
                  activeAddons={activeAddons}
                  billingInterval={billingInterval}
                  onAddonCheckoutSuccess={onAddonCheckoutSuccess}
                  openTrialModal={openTrialModal}
                  hasActiveLicence={hasActiveLicence}
                  trialAvailable={trialAvailable}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      ) : (
        <Flex gap="md" wrap="wrap" justify="center" align="stretch">
          {addons.map((addon) => (
            <AvailableAddonCard
              key={addon.name}
              addon={addon}
              activeAddons={activeAddons}
              billingInterval={billingInterval}
              onAddonCheckoutSuccess={onAddonCheckoutSuccess}
              openTrialModal={openTrialModal}
              hasActiveLicence={hasActiveLicence}
              trialAvailable={trialAvailable}
            />
          ))}
        </Flex>
      )}
      <Space h="md" />
    </div>
  );
};

export default AvailableAddons;
